import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { getCustomerCompanies } from "../../../../services/staffingService";
import DashboardSidebar from "../agencyDashboardSidebar";
import CompanyDetailsTab from "./tabs/details";
import CompanyDocsTab    from "./tabs/documents";
import CompanyTeamTab    from "./tabs/customerInvite";
import {
  ArrowLeft, Building2, Phone, Mail, DollarSign, Globe,
  Settings, FileText, Users, Plus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TABS = [
  { id: "details",   label: "Details",   icon: Settings },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "team",      label: "Team",      icon: Users },
];

export default function CustomerDetail() {
  const { providerCompanyId, customerCompanyLocalId } = useParams();
  const history = useHistory();

  const [showSidebar, setShowSidebar] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState("details");

  useEffect(() => {
    async function fetchData() {
      if (!providerCompanyId || !customerCompanyLocalId) return;
      try {
        const resp = await getCustomerCompanies(providerCompanyId);
        const companies = resp.data.customerCompanies || [];
        const found = companies.find((c) => c._id === customerCompanyLocalId);
        if (!found) {
          setError("Customer not found.");
        } else {
          setCompanyData(found);
        }
      } catch (err) {
        console.error("Failed to fetch customer:", err);
        setError("Failed to load customer data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [providerCompanyId, customerCompanyLocalId]);

  /* tabs mutate list state — normalise to local companyData */
  const handleCompaniesUpdate = (updatedCompanies) => {
    if (updatedCompanies && updatedCompanies.length > 0) {
      setCompanyData(updatedCompanies[0]);
    }
  };

  const displayName = companyData
    ? companyData.companyFriendlyName || companyData.companyLegalName || "Customer"
    : "";

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Back button */}
          <button
            onClick={() => history.push(`/provider-company/${providerCompanyId}/crm`)}
            className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft size={16} />
            Back to Customers
          </button>

          {/* Loading skeleton */}
          {loading && (
            <div className="space-y-4">
              <div className="h-8 w-64 animate-pulse rounded bg-muted" />
              <div className="h-32 animate-pulse rounded-xl bg-muted" />
              <div className="h-10 w-96 animate-pulse rounded bg-muted" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-48 animate-pulse rounded-xl bg-muted" />
                <div className="h-48 animate-pulse rounded-xl bg-muted" />
              </div>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Main content */}
          {!loading && !error && companyData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* ===== PROFILE HEADER ===== */}
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <div className="border-b border-border bg-primary/5 px-6 py-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                      {/* Logo / Icon */}
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10">
                        {companyData.logoUrl ? (
                          <img
                            src={companyData.logoUrl}
                            alt={displayName}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Building2 size={26} className="text-primary" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                          <h2 className="text-xl font-bold text-card-foreground">{displayName}</h2>
                          {companyData.facilityType && (
                            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                              {companyData.facilityType}
                            </span>
                          )}
                        </div>
                        {companyData.companyLegalName && companyData.companyLegalName !== companyData.companyFriendlyName && (
                          <p className="mt-0.5 text-sm text-muted-foreground">{companyData.companyLegalName}</p>
                        )}
                      </div>
                    </div>

                    {/* New Job button */}
                    <button
                      onClick={() =>
                        history.push(`/provider-company/${providerCompanyId}/crm/${companyData._id}/job/new`)
                      }
                      className="flex items-center gap-2 rounded-lg bg-brand-pink px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-pink/90"
                    >
                      <Plus size={15} />
                      New Job
                    </button>
                  </div>
                </div>

                {/* Quick info chips */}
                <div className="flex flex-wrap gap-2 px-6 py-4">
                  {companyData.contact?.contactPersonName && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground">
                      <Users size={12} /> {companyData.contact.contactPersonName}
                    </span>
                  )}
                  {companyData.contact?.phone && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground">
                      <Phone size={12} /> {companyData.contact.phone}
                    </span>
                  )}
                  {companyData.contact?.email && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground">
                      <Mail size={12} /> {companyData.contact.email}
                    </span>
                  )}
                  {companyData.preferences?.hourlyBillingRate && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                      <DollarSign size={12} /> ${companyData.preferences.hourlyBillingRate}/hr
                    </span>
                  )}
                  {companyData.companyWebsiteUrl && (
                    <a
                      href={companyData.companyWebsiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground hover:bg-muted"
                    >
                      <Globe size={12} /> Website
                    </a>
                  )}
                </div>
              </div>

              {/* ===== TAB BAR ===== */}
              <div className="border-b border-border">
                <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Customer tabs">
                  {TABS.map(({ id: tabId, label, icon: Icon }) => (
                    <button
                      key={tabId}
                      onClick={() => setActiveTab(tabId)}
                      className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                        activeTab === tabId
                          ? "border-brand-pink text-brand-pink"
                          : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                      }`}
                    >
                      <Icon size={15} />
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* ===== TAB CONTENT ===== */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {activeTab === "details" && (
                    <div className="rounded-xl border border-border bg-card shadow-sm p-6">
                      <CompanyDetailsTab
                        initialCompanyData={companyData}
                        providerCompanyId={providerCompanyId}
                        customerCompanies={[companyData]}
                        filteredCustomerCompanies={[companyData]}
                        setCustomerCompanies={handleCompaniesUpdate}
                        setFilteredCustomerCompanies={handleCompaniesUpdate}
                      />
                    </div>
                  )}

                  {activeTab === "documents" && (
                    <div className="rounded-xl border border-border bg-card shadow-sm p-6">
                      <CompanyDocsTab
                        companyData={companyData}
                        providerCompanyId={providerCompanyId}
                        customerCompanies={[companyData]}
                        filteredCustomerCompanies={[companyData]}
                        setCustomerCompanies={handleCompaniesUpdate}
                        setFilteredCustomerCompanies={handleCompaniesUpdate}
                      />
                    </div>
                  )}

                  {activeTab === "team" && (
                    <div className="rounded-xl border border-border bg-card shadow-sm p-6">
                      <CompanyTeamTab
                        companyData={companyData}
                        providerCompanyId={providerCompanyId}
                        customerCompanies={[companyData]}
                        filteredCustomerCompanies={[companyData]}
                        setCustomerCompanies={handleCompaniesUpdate}
                        setFilteredCustomerCompanies={handleCompaniesUpdate}
                      />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Internal ID */}
              <div className="rounded-lg bg-muted/50 px-4 py-2">
                <p className="text-xs text-muted-foreground">
                  Customer ID: <span className="font-mono">{companyData._id}</span>
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
